import { ITrackerStatus } from "@easypost/api";
import { TrackableFulfillmentType } from "@redotech/redo-model/conversation/message-parsers/track-order-message-parser";
import { prettyTrackerStatus } from "@redotech/redo-model/trackable/delivery-estimation";
import classNames from "classnames";
import { memo } from "react";
import { RedoBadge } from "../../arbiter-components/badge/redo-badge";
import { Divider } from "../../divider";
import { Flex } from "../../flex";
import { ExternalLink } from "../../link";
import { ProductInfoCardButton } from "../../product-info-card-button";
import { Text } from "../../text";
import * as orderTrackingInfoMessageCss from "./order-tracking-info-message.module.css";

export const OrderTrackingInfoMessage = memo(function OrderTrackingInfoMessage({
  trackableFulfillmentsToDisplay,
  readOnly = false,
  storeUrl,
  messageSide = "left",
}: {
  trackableFulfillmentsToDisplay: TrackableFulfillmentType;
  readOnly?: boolean;
  storeUrl: string | undefined;
  messageSide?: "left" | "right";
}) {
  return trackableFulfillmentsToDisplay.map((fulfillment, index) => {
    const mostRecentStatus =
      fulfillment.trackingDetails?.[fulfillment.trackingDetails.length - 1]
        ?.status;

    return (
      <Flex
        className={classNames(
          orderTrackingInfoMessageCss.orderTrackingInfoContainer,
          {
            [orderTrackingInfoMessageCss.messageSideLeft]:
              messageSide === "left",
            [orderTrackingInfoMessageCss.messageSideRight]:
              messageSide === "right",
          },
        )}
        dir="column"
        gap="xl"
        justify="flex-start"
        key={index}
        p="xl"
      >
        <Flex dir="column" gap="2xl">
          {mostRecentStatus && (
            <Flex dir="column" gap="sm">
              <Text fontSize="sm" fontWeight="medium" textColor="secondary">
                Status
              </Text>
              <RedoBadge
                color="blue"
                size="sm"
                text={
                  prettyTrackerStatus[mostRecentStatus as ITrackerStatus] ??
                  mostRecentStatus
                }
                type="primary"
              />
            </Flex>
          )}
          <Flex>
            <StaticTrackingDetailsComponent
              details={fulfillment.trackingDetails}
              estimatedDeliveryDate={fulfillment.estimatedDeliveryDate}
              orderCreatedAt={fulfillment.orderCreatedAt}
            />
          </Flex>
          <Divider />
          <Flex justify="space-between">
            <Flex dir="column" gap="xs" overflow="hidden">
              <Text fontSize="xs" fontWeight="medium" textColor="secondary">
                Tracking
              </Text>
              {fulfillment.redoTrackingPageLink ? (
                <ExternalLink
                  iconClassName={orderTrackingInfoMessageCss.externalLinkIcon}
                  showIcon
                  url={fulfillment.redoTrackingPageLink}
                >
                  <Text
                    fontSize="xs"
                    fontWeight="medium"
                    overflow="hidden"
                    textColor="primary"
                    textOverflow="ellipsis"
                    whiteSpace="nowrap"
                  >
                    {fulfillment.trackingNumber ?? "View"}
                  </Text>
                </ExternalLink>
              ) : (
                "-"
              )}
            </Flex>
            <Flex dir="column" gap="xs">
              <Text fontSize="xs" fontWeight="medium" textColor="secondary">
                Carrier
              </Text>
              <Text fontSize="xs" fontWeight="medium" textColor="primary">
                {fulfillment.carrier ?? "-"}
              </Text>
            </Flex>
          </Flex>
        </Flex>
        <Flex dir="column" gap="sm">
          <Text fontSize="sm" fontWeight="medium" textColor="secondary">
            Order summary
          </Text>
          <Flex dir="column" gap="xl">
            {fulfillment.lineItems.map((product, index) => (
              <ProductInfoCardButton
                key={index}
                onClickUrlOverride={fulfillment.redoTrackingPageLink}
                openInNewTab
                product={{
                  ...product,
                  variantId: product.variantId ?? undefined,
                  variantTitle: product.variantTitle ?? undefined,
                  url: product.url ?? undefined,
                  handle: product.handle ?? undefined,
                  imageUrl: product.imageUrl ?? undefined,
                }}
                storeUrl={storeUrl}
              />
            ))}
          </Flex>
        </Flex>
      </Flex>
    );
  });
});

const StaticTrackingDetailsComponent = memo(
  function StaticTrackingDetailsComponent({
    details,
    estimatedDeliveryDate,
    orderCreatedAt,
  }: {
    details?: { status: string; datetime: string }[];
    estimatedDeliveryDate?: string | null;
    orderCreatedAt?: string;
  }) {
    const detailDate = (detail: { datetime: string }) => {
      try {
        if (!detail.datetime) return undefined;
        const date = Temporal.Instant.from(detail.datetime);
        const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        return date.toLocaleString(undefined, {
          dateStyle: "medium",
          timeStyle: "short",
          timeZone: clientTimeZone,
        });
      } catch (e) {
        console.error("Error parsing date", e);
        return "Unknown";
      }
    };

    const fulfillmentShipped = details?.find(
      (detail) => detail.status === "in_transit",
    );
    const fulfillmentDelivered = details?.find(
      (detail) => detail.status === "delivered",
    );

    const FulfillmentDetailEntry = ({
      title,
      date,
      hasCircle,
    }: {
      title: string;
      date?: string;
      hasCircle: boolean;
    }) => (
      <Flex
        className={orderTrackingInfoMessageCss.historyEntry}
        dir="column"
        gap="xxs"
        justify="center"
        pl="3xl"
        position="relative"
      >
        {hasCircle && (
          <span className={orderTrackingInfoMessageCss.filledCircle} />
        )}
        <Text fontSize="xs" fontWeight="semibold" textColor="primary">
          {title}
        </Text>
        <Text fontSize="xs" fontWeight="regular" textColor="quaternary">
          {date ?? ""}
        </Text>
      </Flex>
    );

    return (
      <Flex dir="column" gap="sm">
        <FulfillmentDetailEntry
          date={
            orderCreatedAt
              ? detailDate({ datetime: orderCreatedAt })
              : undefined
          }
          hasCircle
          title="Order Placed"
        />
        <FulfillmentDetailEntry
          date={fulfillmentShipped ? detailDate(fulfillmentShipped) : undefined}
          hasCircle={!!fulfillmentShipped}
          title="Shipped"
        />
        <FulfillmentDetailEntry
          date={
            fulfillmentDelivered ? detailDate(fulfillmentDelivered) : undefined
          }
          hasCircle={!!fulfillmentDelivered}
          title="Delivered"
        />
      </Flex>
    );
  },
);
